import Page from "components/common/layout/Page";
import React from "react";
import Button from "../../components/common/ui/buttons/Button/Button";
import styled from "styled-components";
import { useTranslate } from "hooks/common/useTranslate/useTranslate";
import MetaDescription from "components/common/MetaDescription/MetaDescription";
import useLocale from "hooks/common/useLocale/useLocale";
import featureImg from "../../images/huballas_muuttanut.png";
import { colors, H1, H3 } from "./layout/Styles";

const HeaderSection = styled.section`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 1.5rem;
    padding: 3rem 0 2rem;
`;

const HeaderTitle = styled(H1)`
    font-size: 3rem;
    line-height: 3.5rem;
`;

const HeaderDescription = styled.p`
    max-width: 48rem;
    margin: 0;
    font-size: 1.125rem;
    font-weight: 500;
    line-height: 1.5rem;
    color: ${colors.text};
`;

const HeaderAction = styled.div`
    display: flex;
    align-items: center;
`;

const HeaderButton = styled(Button)`
    height: auto;
    padding: 1.5rem;
    gap: 0.5rem;
`;

const FeatureSection = styled.section`
    margin-top: 2rem;
    margin-bottom: 4rem;
`;

const SectionTitle = styled(H3)`
    margin: 0rem;
    font-size: 1.5rem;
    line-height: 2rem;
    font-weight: 500;
`;

const FeatureCard = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 368px) minmax(0, 1fr);
    gap: 2rem;
    padding-top: 1.5rem;
`;

const FeatureMedia = styled.div`
    width: 100%;
    min-height: 198px;
    background: linear-gradient(135deg, rgba(62, 86, 96, 0.12), rgba(0, 154, 150, 0.08));
    border: 1px solid ${colors.separator};
    background-image: url(${featureImg});
`;

const FeatureContent = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-height: 198px;
`;

const FeatureText = styled.p`
    margin: 0;
    max-width: 26.5rem;
    font-size: 18px;
    font-style: normal;
    font-weight: 500;
    line-height: 24px;
    color: ${colors.text};

    ul {
        margin: 0;
        padding-left: 1.5rem;
    }
`;

const FeatureAction = styled.a`
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 1rem;
    width: fit-content;
    font-size: 1rem;
    line-height: 1.5rem;
    font-weight: 600;
    color: ${colors.text};
    text-decoration: none;

    &:hover {
        text-decoration: underline;
    }
`;

const FeatureActionIcon = styled.span`
    display: inline-flex;
    color: ${colors.primaryAction};
`;

const linkToNewHuballas = (locale: string) =>
    `https://beta.palvelut.datahub.fi/${locale}/kayttotilastot`;

const ExtPageIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
    >
        <g clipPath="url(#clip0_10273_853)">
            <path
                d="M19 19H5V5H12V3H5C3.89 3 3 3.9 3 5V19C3 20.1 3.89 21 5 21H19C20.1 21 21 20.1 21 19V12H19V19ZM14 3V5H17.59L7.76 14.83L9.17 16.24L19 6.41V10H21V3H14Z"
                fill="currentColor"
            />
        </g>
        <defs>
            <clipPath id="clip0_10273_853">
                <rect width="24" height="24" fill="white" />
            </clipPath>
        </defs>
    </svg>
);

const HuballasHasMoved = () => {
    const locale = useLocale();
    const translate = useTranslate();
    const huballasUrl = linkToNewHuballas(locale);

    return (
        <>
            <MetaDescription description={translate("huballas.page.description")} />
            <Page>
                <HeaderSection>
                    <HeaderTitle>{translate("huballas.hasMoved.title")}</HeaderTitle>
                    <HeaderDescription>
                        {translate("huballas.hasMoved.description.1")}
                        <br />
                        {translate("huballas.hasMoved.description.2")}{" "}
                        <a href={huballasUrl} target="_blank" rel="noopener noreferrer">
                            {huballasUrl}
                        </a>
                    </HeaderDescription>
                    <HeaderAction>
                        <a href={huballasUrl} target="_blank" rel="noopener noreferrer">
                            <HeaderButton>
                                <span>{translate("huballas.hasMoved.cta")}</span>
                                <ExtPageIcon />
                            </HeaderButton>
                        </a>
                    </HeaderAction>
                </HeaderSection>

                <FeatureSection>
                    <SectionTitle>{translate("huballas.hasMoved.featureTitle")}</SectionTitle>
                    <FeatureCard>
                        <FeatureMedia aria-label={translate("huballas.hasMoved.featureImageAlt")} />
                        <FeatureContent>
                            <FeatureText>
                                <ul>
                                    <li>{translate("huballas.hasMoved.featureDescription.1")}</li>
                                    <li>{translate("huballas.hasMoved.featureDescription.2")}</li>
                                </ul>
                            </FeatureText>
                            <FeatureAction
                                href={huballasUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {translate("huballas.hasMoved.featureCta")}
                                <FeatureActionIcon>
                                    <ExtPageIcon />
                                </FeatureActionIcon>
                            </FeatureAction>
                        </FeatureContent>
                    </FeatureCard>
                </FeatureSection>
            </Page>
        </>
    );
};

export default HuballasHasMoved;
